import { useState, useMemo } from "react";

export interface Coder {
  id: number;
  aia_coder_id: string;
  first_name: string;
  last_name: string;
  email: string;
  enterpriseId: number;
  enterprise?: {
    nom: string;
    country: string;
    language: string;
    sendContracts?: boolean;
  };
}

export interface CoderFilters {
  search: string;
  enterprise: string;
  country: string;
  language: string;
  sortBy: "name" | "email" | "enterprise" | "country" | "";
  sortOrder: "asc" | "desc";
}

export const useCoderFilters = (
  coders: Coder[],
  initialFilters?: Partial<CoderFilters>,
) => {
  const [filters, setFilters] = useState<CoderFilters>({
    search: "",
    enterprise: "",
    country: "",
    language: "",
    sortBy: "",
    sortOrder: "asc",
    ...initialFilters,
  });

  /**
   * Only sorts. `search`, `enterprise`, `country` and `language` are sent to the
   * API by the page, which filters the whole table — matching them again here
   * would be redundant, and matching them *only* here would restrict the result
   * to whichever rows the current page happens to hold.
   *
   * The sort still applies to the current page alone; there is no sort control
   * wired up on the Coders page today.
   */
  const filteredCoders = useMemo(() => {
    let filtered = [...coders];

    // Sort the results
    if (filters.sortBy) {
      filtered.sort((a, b) => {
        let aValue: string = "";
        let bValue: string = "";

        switch (filters.sortBy) {
          case "name":
            aValue = `${a.first_name} ${a.last_name}`;
            bValue = `${b.first_name} ${b.last_name}`;
            break;
          case "email":
            aValue = a.email;
            bValue = b.email;
            break;
          case "enterprise":
            aValue = a.enterprise?.nom || "";
            bValue = b.enterprise?.nom || "";
            break;
          case "country":
            aValue = a.enterprise?.country || "";
            bValue = b.enterprise?.country || "";
            break;
        }

        const comparison = aValue.localeCompare(bValue);
        return filters.sortOrder === "desc" ? -comparison : comparison;
      });
    }

    return filtered;
  }, [coders, filters]);

  const updateFilter = (key: keyof CoderFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateSort = (sortBy: CoderFilters["sortBy"]) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder:
        prev.sortBy === sortBy && prev.sortOrder === "asc" ? "desc" : "asc",
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      enterprise: "",
      country: "",
      language: "",
      sortBy: "",
      sortOrder: "asc",
    });
  };

  const hasActiveFilters = useMemo(() => {
    return (
      filters.search ||
      filters.enterprise ||
      filters.country ||
      filters.language
    );
  }, [filters]);

  return {
    filters,
    filteredCoders,
    updateFilter,
    updateSort,
    resetFilters,
    hasActiveFilters,
  };
};
